import { fromJS } from 'immutable';
import { getUserFullName, getUserShortName } from 'helpers/userHelper';
import { getPermissions } from 'selectors/entities/rbacSelectors';
import { getFiltersByKey } from 'selectors/settingsSelectors';
import { getLegalEntities } from 'selectors/entities/legalEntitiesSelectors';
import { filterSearch } from 'helpers/utilHelpers';
import { createImmutableSelector } from 'util/reselectUtils';

export const getAllUsersFromState = (state) => state.getIn(['entities', 'user', 'users']);

export const getUserOnlineArray = (state) => state.getIn(['entities', 'user', 'online']);
export const getUserOnline = (state, userId) => state.getIn(['entities', 'user', 'online'])?.has(userId);

const getUsersWithoutAdmin = createImmutableSelector([getAllUsersFromState], (users) =>
  users.filter((user) => user.get('userType') !== 'admin'),
);

const getUsersOnlyActiveOrWithInvited = createImmutableSelector(
  [getUsersWithoutAdmin, (_, props) => props?.invited],
  (usersWithoutAdmin, invited) =>
    usersWithoutAdmin.filter((user) =>
      invited ? ['active', 'invited'].includes(user.get('status')) : user.get('status') === 'active',
    ),
);

// Returns active users without admins
export const getUsers = createImmutableSelector(
  [getUsersOnlyActiveOrWithInvited, (_, props) => props?.userSearch],
  (users, userSearch) => {
    let newUsers = users;

    if (userSearch !== undefined) {
      newUsers = newUsers.filter((user) => filterSearch(getUserFullName(user), userSearch));
    }

    return newUsers.sort((a, b) => {
      const nameA = getUserShortName(a).toLowerCase();
      const nameB = getUserShortName(b).toLowerCase();
      return nameA < nameB ? -1 : 1;
    });
  },
);

// Returns all users without admins
export const getAllUsers = createImmutableSelector(
  [getUsersWithoutAdmin, (_, props) => props?.userSearch],
  (usersWithoutAdmin, userSearch) => {
    let newUsers = usersWithoutAdmin;
    if (userSearch !== undefined) {
      newUsers = newUsers.filter((user) => filterSearch(getUserFullName(user) + user.get('email'), userSearch));
    }

    return newUsers.sort((a, b) => {
      const nameA = getUserFullName(a).toLowerCase(),
        nameB = getUserFullName(b).toLowerCase();
      return nameA < nameB ? -1 : 1;
    });
  },
);

// Returns all user client profiles
export const getClientProfilesFromState = (state) => state.getIn(['entities', 'user', 'clientProfiles']);

export const getClientProfiles = createImmutableSelector(
  [getClientProfilesFromState, (_, props) => props?.userSearch],
  (clientProfiles, userSearch) => {
    if (userSearch === undefined) {
      return clientProfiles;
    }

    return clientProfiles.filter((user) => filterSearch(getUserFullName(user), userSearch));
  },
);

export const getActiveClientProfilesFromState = (state) =>
  state.getIn(['entities', 'user', 'clientProfiles'])?.filter((user) => user.get('status') === 'active');

export const getActiveClientProfiles = createImmutableSelector(
  [getActiveClientProfilesFromState, (state, props) => props?.userSearch],
  (clientProfiles, userSearch) => clientProfiles.filter((user) => filterSearch(getUserFullName(user), userSearch)),
);

export const getSubordinates = createImmutableSelector(
  [getUsersOnlyActiveOrWithInvited, (_, props) => props?.userId],
  (users, userId) => {
    if (!userId) {
      return users;
    }

    return users.filter((user) => user.get('superiorId') === userId);
  },
);

// Returns all departments
export const getDepartmentsFromState = (state) => state.getIn(['entities', 'user', 'departments']);
// Returns all departments from state filtered by departmentSearch if needed
export const getDepartments = createImmutableSelector(
  [getDepartmentsFromState, (_, props) => props?.departmentSearch],
  (departments, departmentSearch) => {
    let newDepartments = departments;
    if (departmentSearch) {
      newDepartments = newDepartments.filter((department) => filterSearch(department.get('name'), departmentSearch));
    }

    return newDepartments.sortBy((position) => position.get('name'));
  },
);

// Returns users with departments
export const getUsersWithDepartment = createImmutableSelector([getUsers, getDepartments], (users, departments) => {
  return users.map((user) => user.set('department', departments.get(user.get('departmentId'))));
});

// Returns all users with client profiles
export const getUsersWithClients = createImmutableSelector(
  [getUsersWithDepartment, getClientProfiles],
  (getUsersWithDepartment, clientProfiles) => getUsersWithDepartment.merge(clientProfiles),
);

export const getDepartamentById = (state, { departmentId }) =>
  state.getIn(['entities', 'user', 'departments', departmentId]);

export const getPositionsFromState = (state) => state.getIn(['entities', 'user', 'positions']);

// Returns all positions from state filtered by positionSearch if needed
export const getPositions = (state, props = {}) =>
  state
    .getIn(['entities', 'user', 'positions'])
    .filter((position) => filterSearch(position.get('name'), props.positionSearch))
    .sortBy((position) => position.get('name'));

// Returns all roles from state, filtered by rolesSearch if needed
export const getRoles = (state, props = {}) =>
  state.getIn(['entities', 'user', 'roles']).filter((role) => filterSearch(role.get('name'), props.rolesSearch));

// Returns all roles from state, filtered by rolesSearch if needed
export const getRolesByType = createImmutableSelector([getRoles, (state, props) => props?.type], (roles, type) => {
  if (type === 'user') {
    return roles.filter((role) => ['client', 'contact', 'guest'].indexOf(role.get('alias')) === -1);
  }

  if (type === 'client') {
    return roles.filter((role) => ['client', 'contact'].indexOf(role.get('alias')) !== -1);
  }

  return roles;
});

// Returns all freelancers from the state filtered by freelancersSearch if needed
export const getFreelancers = (state, props = {}) =>
  state
    .getIn(['entities', 'user', 'freelancers'])
    .filter((freelancer) =>
      filterSearch(
        getUserFullName(freelancer) + freelancer.get('options').toString() + freelancer.get('cmsList').join(''),
        props.freelancersSearch,
      ),
    );

// Returns cms list
export const getCmsList = (state) => state.getIn(['entities', 'user', 'cmsList']);

// Returns salaries
export const getSalaries = (state) => state.getIn(['entities', 'user', 'salaries']);

// Returns structure chart
export const getStructureChart = (state) => state.getIn(['entities', 'user', 'structureChart']);

export const getCompanies = (state) => state.getIn(['entities', 'user', 'companies']);

// Returns company by specified companyId
export const getCompanyById = (state, { companyId }) => state.getIn(['entities', 'user', 'companies', companyId]);

// Returns user by specified userId
export const getUserById = (state, { userId }) => state.getIn(['entities', 'user', 'users', userId]);
// Returns user by specified userId
export const getClientProfileById = (state, { userId }) => state.getIn(['entities', 'user', 'clientProfiles', userId]);

// Returns department by specified departmentId
export const getDepartmentById = (state, { departmentId }) =>
  state.getIn(['entities', 'user', 'departments', departmentId]);

// Returns position by specified positionId
export const getPositionById = (state, { positionId }) => state.getIn(['entities', 'user', 'positions', positionId]);

// Returns role by specified roleId
export const getRoleById = (state, { roleId }) => state.getIn(['entities', 'user', 'roles', roleId]);

// Returns freelancer by specified freelancerId
export const getFreelancerById = (state, { freelancerId }) =>
  state.getIn(['entities', 'user', 'freelancers', freelancerId]);

// Returns all user client profiles and active users without admins
export const getUsersAndClients = createImmutableSelector([getUsers, getClientProfiles], (users, clients) => {
  if (users && clients) {
    return users.merge(clients);
  }
  if (users) {
    return users;
  }
  if (clients) {
    return clients;
  }
  return null;
});

// Returns all user client profiles and all users without admins
export const getAllUsersAndClients = createImmutableSelector([getAllUsers, getClientProfiles], (users, clients) => {
  if (users && clients) {
    return users.merge(clients);
  }
  if (users) {
    return users;
  }
  if (clients) {
    return clients;
  }
  return null;
});

// Returns filtered users
export const getFilteredUsers = createImmutableSelector(
  [getAllUsers, getFiltersByKey, getPositions, getDepartments],
  (users, filters, positions, departments) => {
    if (filters) {
      if (filters.get('status') === 'active') {
        users = users.filter((user) => ['active', 'invited'].includes(user.get('status')));
      } else if (filters.get('status') !== 'all') {
        users = users.filter((user) => user.get('status') === filters.get('status'));
      }
    } else {
      users = users.filter((user) => user.get('status') === 'active');
    }
    return users.map((user) =>
      user.merge(
        fromJS({
          position: positions.get(user.get('positionId')),
          department: departments.get(user.get('departmentId')),
        }),
      ),
    );
  },
);

// Selector returns sorted users by short name
export const getSortedUsersByShortName = createImmutableSelector(getUsers, (users) =>
  users.sort((a, b) => {
    const nameA = getUserShortName(a).toLowerCase(),
      nameB = getUserShortName(b).toLowerCase();
    return nameA > nameB ? 1 : -1;
  }),
);
// Selector returns users who are a leader at least in one project
export const getLeaders = createImmutableSelector(getSortedUsersByShortName, (users) =>
  users.filter((user) => user.get('isLeader')),
);

// Selector returns users who are a responsible at least in one project
export const getResponsible = createImmutableSelector(getSortedUsersByShortName, (users) =>
  users.filter((user) => user.get('isResponsible')),
);

// Selector returns users who are a responsible at least in one project
export const getManagers = createImmutableSelector(getSortedUsersByShortName, (users) =>
  users.filter((user) => user.get('isManager')),
);

export const getLastTask = (state) => state.getIn(['entities', 'user', 'lastTask']);
//
export const getUsersWithLastTask = createImmutableSelector([getUsers, getLastTask], (users, lastTask) => {
  if (lastTask) {
    return users
      .sortBy((user) => user.get('firstname'))
      .map((user) => {
        return user.set(
          'lastTask',
          lastTask.find((item) => item?.get('userId') === user?.get('userId')),
        );
      })
      .sortBy((user) => user.getIn(['lastTask', 'status']));
  }
});

// Returns user roles with related users
export const getRolesSummary = createImmutableSelector(
  [getRolesByType, getUsers, getPermissions],
  (roles, users, permissions) => {
    return roles.map((role) => {
      const roleUsers = users.filter((user) => {
        return user.get('role') === role.get('roleId').toString() && user.get('status') === 'active';
      });

      return role.merge(
        fromJS({
          users: roleUsers,
          permissions: role.get('permissions').map((permission) => {
            return permissions.get(permission);
          }),
        }),
      );
    });
  },
);

// Returns company by specified companyId
export const getCompanyByIdWithSummary = createImmutableSelector(
  [getCompanyById, getLegalEntities],
  (company, legalEntities) => {
    if (!company?.get('legalEntities')?.size) {
      return company;
    }

    return company.set(
      'legalEntities',
      company.get('legalEntities').map((entityId) => legalEntities.get(entityId)),
    );
  },
);
